import '@babylonjs/core/Animations/animatable';
import { Color3 } from '@babylonjs/core/Maths/math';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { CustomMaterial } from '@babylonjs/materials/custom/customMaterial';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';

import { System } from './system';

import sparkleTexture from '../../docs/assets/images/T_Star.png';
import surfaceTexture from '../../docs/assets/images/T_SunSurface.png';

interface Sky {
    color0: Color3
    color1: Color3
    color2: Color3
    name: string
}

const SKYS: Sky[] = [
    {
        color0: new Color3(31 / 255, 21 / 255, 33 / 255),
        color1: new Color3(112 / 255, 68 / 255, 166 / 255),
        color2: new Color3(48 / 255, 200 / 255, 200 / 255),
        name: 'Orion'
    },
    {
        color0: new Color3(12 / 255, 38 / 255, 36 / 255),
        color1: new Color3(102 / 255, 232 / 255, 180 / 255),
        color2: new Color3(122 / 255, 252 / 255, 102 / 255),
        name: 'Eagle'
    },
    {
        color0: new Color3(33 / 255, 28 / 255, 11 / 255),
        color1: new Color3(222 / 255, 162 / 255, 48 / 255),
        color2: new Color3(220 / 255, 80 / 255, 80 / 255),
        name: 'Trifid'
    },
    {
        color0: new Color3(20 / 255, 18 / 255, 71 / 255),
        color1: new Color3(66 / 255, 76 / 255, 200 / 255),
        color2: new Color3(220 / 255, 50 / 255, 220 / 255),
        name: 'Helix'
    },
    {
        color0: new Color3(26 / 255, 36 / 255, 25 / 255),
        color1: new Color3(112 / 255, 192 / 255, 96 / 255),
        color2: new Color3(220 / 255, 220 / 255, 48 / 255),
        name: 'Dumbbell'
    },
    {
        color0: new Color3(50 / 255, 36 / 255, 51 / 255),
        color1: new Color3(154 / 255, 68 / 255, 106 / 255),
        color2: new Color3(24 / 255, 150 / 255, 200 / 255),
        name: 'Tarantula'
    },
]

export class EnvironmentSystem extends System {

    public sparkleTexture: Texture;

    public surfaceTexture: Texture;

    /**
     * Creates a new System
     * @param canvas Element where the scene will be drawn
     */
    constructor(canvas: HTMLCanvasElement) {
        super(canvas);
        this.addRibbonMaterial();
        this.sparkleTexture = new Texture(sparkleTexture, this.scene);
        this.surfaceTexture = new Texture(surfaceTexture, this.scene);
    }

    public skyColor: Color3;

    public skyColorHex: string;

    public sky: Sky = SKYS[0];

    protected planetMaterial: StandardMaterial;

    public setSky(design: number) {
        this.sky = SKYS[design];
        const { color1, color2 } = this.sky;
        this.skyColor = color1;
        this.planetMaterial.emissiveFresnelParameters.leftColor = color1;
        this.planetMaterial.emissiveFresnelParameters.rightColor = color2;
        const lightColor = color1.multiplyByFloats(2, 2, 2).minimizeInPlaceFromFloats(1, 1, 1);
        this.skyColorHex = lightColor.toHexString();
    }

    public ribbonMaterial: CustomMaterial;

    private addRibbonMaterial() {
        this.ribbonMaterial = new CustomMaterial("ribbonMaterial", this.scene);
        // this.ribbonMaterial.alpha = 0.5;

        this.ribbonMaterial.AddUniform('color0', 'vec3', Color3.White());
        this.ribbonMaterial.AddUniform('color1', 'vec3', Color3.White());
        this.ribbonMaterial.AddUniform('color2', 'vec3', Color3.Black());
        this.ribbonMaterial.Vertex_Definitions("varying vec3 vPosition;");
        this.ribbonMaterial.Fragment_Definitions("varying vec3 vPosition;");
        this.ribbonMaterial.Vertex_MainBegin("vPosition = position;");
        this.ribbonMaterial.Fragment_Before_FragColor(
            `
                float x = vPosition.x;
                float z = vPosition.z;
                float y = vPosition.y;

                // SUN LIGHT
                float starLight = (y * 1.) + 1.0;

                // SQUARE GRID LINES
                float frequency = 1.0;
                float power = 10.0;
                float gridX = pow(1. - abs(sin(x * frequency)), power);
                float gridZ = pow(1. - abs(sin(z * frequency)), power);
                float grid = min(gridX + gridZ, 1.);

                vec3 gridColor = mix(color1, color2, y);

                vec3 colorLight = color0 * starLight;
                color.rgb = mix(colorLight, gridColor, grid/2.);

                // float blackHole = 2. - (y / 10.);
                // blackHole = min(max(blackHole, 0.), 1.);
                // color.rgb *= blackHole;
            `
        );

        this.ribbonMaterial.onBindObservable.add(() => {
            const { color0, color1, color2 } = this.sky;
            const effect = this.ribbonMaterial.getEffect();
            effect.setColor3('color0', color0);
            effect.setColor3('color1', color1);
            effect.setColor3('color2', color2);
        });
    }
}